import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const MAX_VISIBLE_LINES = 6;
const DEFAULT_LINE_HEIGHT_PX = 24;

const measureOverflows = (element: HTMLElement): boolean => {
    const lineHeightPx = parseFloat(getComputedStyle(element).lineHeight);
    const lineHeight = Number.isFinite(lineHeightPx) ? lineHeightPx : DEFAULT_LINE_HEIGHT_PX;
    const lines = Math.ceil(element.scrollHeight / lineHeight);

    return lines > MAX_VISIBLE_LINES;
};

export const useOnboardingAgentUserMessage = (text: string) => {
    const textRef = useRef<HTMLParagraphElement>(null);
    const [overflows, setOverflows] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const recomputeOverflow = useCallback((): void => {
        const el = textRef.current;
        if (!el) {
            return;
        }
        setOverflows(measureOverflows(el));
    }, []);

    useLayoutEffect(() => {
        setExpanded(false);
        recomputeOverflow();
    }, [recomputeOverflow, text]);

    useEffect(() => {
        const el = textRef.current;
        if (!el || typeof ResizeObserver === "undefined") {
            return;
        }
        const observer = new ResizeObserver(() => recomputeOverflow());
        observer.observe(el);

        return () => observer.disconnect();
    }, [recomputeOverflow]);

    const isClamped = overflows && !expanded;

    const handleToggleExpanded = (): void => {
        setExpanded((previous) => !previous);
    };

    return {
        textRef,
        expanded,
        isOverflowing: overflows,
        isClamped,
        handleToggleExpanded,
    };
};
